import { useEffect, useRef } from "react";

import { useAskConversationHistoryEnabled } from "./useAskConversationHistoryEnabled";

import { getAskConversation } from "../../../api/services/defs/internal-api/ai";

import { useAskPerformYardStore } from "../AskPerformYardStore";

/**
 * Re-hydrate the conversation the user was last in, when the panel opens on a
 * fresh page load (PY-13559).
 *
 * The active-conversation pointer lives in `localStorage`, so it outlives the
 * store. Without this, a reload would leave the id pointing at a thread whose
 * messages are gone: the next turn would append to that conversation while
 * sending none of its history to the agent, which reads to the user as the
 * assistant forgetting a conversation it is visibly still in.
 *
 * A failed restore clears the pointer rather than surfacing an error. The
 * conversation may have been deleted, or the pointer may be from another
 * account on a shared machine — in both cases the honest outcome is a new chat,
 * not an error on a panel the user just opened.
 */
export const useRestoreActiveConversation = (): void => {
	const historyEnabled = useAskConversationHistoryEnabled();
	const isPanelOpen = useAskPerformYardStore((state) => state.isPanelOpen);
	const conversationId = useAskPerformYardStore((state) => state.conversationId);
	const hasUserMessages = useAskPerformYardStore((state) => state.messages.some((m) => m.role === "user"));

	// Restore is attempted once per conversation id. Without this an id that
	// 404s would retry on every render the effect's deps touch.
	const attemptedRef = useRef<string | null>(null);

	useEffect(() => {
		if (!historyEnabled || !isPanelOpen || !conversationId) return;
		// The store already holds this thread — either restored, or being built
		// right now by the turn that created it.
		if (hasUserMessages) return;
		if (attemptedRef.current === conversationId) return;

		attemptedRef.current = conversationId;

		const { setIsLoadingConversation, loadConversation, clearActiveConversation } =
			useAskPerformYardStore.getState();

		setIsLoadingConversation(true);
		void getAskConversation(conversationId)
			.then((conversation) => {
				// The user may have started typing a new chat while this was in
				// flight; re-check rather than overwriting what they are doing.
				const current = useAskPerformYardStore.getState();
				if (current.conversationId !== conversationId || current.messages.some((m) => m.role === "user")) {
					setIsLoadingConversation(false);
					return;
				}
				loadConversation(conversation);
			})
			.catch(() => {
				clearActiveConversation();
			});
	}, [historyEnabled, isPanelOpen, conversationId, hasUserMessages]);
};
